/**
 * useDeadlineTimeShift
 * Hook for shifting time to a deadline and triggering pot status transitions.
 * Combines useSetTime and useTriggerPotStatusTransitions for a smooth workflow.
 */
import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSetTime } from '../../development/hooks/useTimeManipulation';
import { useTriggerPotStatusTransitions } from '../../../shared/hooks/useJobs';

export interface UseDeadlineTimeShiftResult {
  shiftTime: (targetTime: Date) => void;
  isPending: boolean;
  isSettingTime: boolean;
  isTransitioning: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  reset: () => void;
}

export function useDeadlineTimeShift(): UseDeadlineTimeShiftResult {
  const [isSuccess, setIsSuccess] = useState(false);
  const queryClient = useQueryClient();

  const setTimeMutation = useSetTime();
  const transitionMutation = useTriggerPotStatusTransitions();

  const shiftTime = useCallback(
    (targetTime: Date) => {
      setIsSuccess(false);
      setTimeMutation.mutate(targetTime, {
        onSuccess: () => {
          // After time is set, trigger pot status transitions
          transitionMutation.mutate(undefined, {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['pots'] });
              setIsSuccess(true);
            },
          });
        },
      });
    },
    [setTimeMutation, transitionMutation, queryClient]
  );

  const reset = useCallback(() => {
    setIsSuccess(false);
    setTimeMutation.reset();
    transitionMutation.reset();
  }, [setTimeMutation, transitionMutation]);

  const isPending = setTimeMutation.isPending || transitionMutation.isPending;
  const isError = setTimeMutation.isError || transitionMutation.isError;
  const error = setTimeMutation.error || transitionMutation.error;

  return {
    shiftTime,
    isPending,
    isSettingTime: setTimeMutation.isPending,
    isTransitioning: transitionMutation.isPending,
    isError,
    error: error instanceof Error ? error : null,
    isSuccess,
    reset,
  };
}
